/**
 * @fileoverview
 * BatchRequestManager for fetching product-card snippets (HTML) and JSON product data
 * from Shopify with batching, caching, debouncing, and abort handling.
 */

import { debounce, scheduler } from "@mzw/utils";

/** Global AbortController for page navigation - aborts in-flight requests on unload. */
const navAbortController = new AbortController();
window.addEventListener("beforeunload", () => navAbortController.abort("navigation"), {
	once: true,
});

/**
 * @typedef {Object} BatchRequestManagerOptions
 * @property {number} [batchSize=6] - Max items to fetch per batch.
 * @property {number} [debounceTime=100] - Debounce delay (ms) for triggering batches.
 * @property {number} [fetchTimeout=8000] - Timeout (ms) before aborting a request.
 * @property {number} [maxCacheSize=100] - Max items to keep in cache (LRU eviction).
 * @property {number} [failureCacheDuration=60000] - How long to cache failed handles (ms).
 */

/**
 * @typedef {Object} ProductData
 * @property {string} handle - The product handle.
 * @property {string} title - The product title.
 * @property {string} [id] - The product ID.
 */

/**
 * Manages batching, caching, and resolving of product-card (HTML) and JSON product data requests.
 */
class BatchRequestManager {
	/** @type {number} */
	#batchSize;
	/** @type {number} */
	#fetchTimeout;
	/** @type {number} */
	#maxCacheSize;
	/** @type {number} */
	#failureCacheDuration;

	// HTML card state
	/** @type {Map<string, string>} */
	#htmlCache = new Map();
	/** @type {Map<string, Array<(card: Element|undefined) => void>>} */
	#htmlPending = new Map();
	/** @type {Set<string>} */
	#htmlInFlight = new Set();
	/** @type {Set<string>} */
	#htmlQueue = new Set();

	// JSON data state
	/** @type {Map<string, ProductData>} */
	#jsonCache = new Map();
	/** @type {Map<string, Array<(data: ProductData|undefined) => void>>} */
	#jsonPending = new Map();
	/** @type {Set<string>} */
	#jsonInFlight = new Set();
	/** @type {Set<string>} */
	#jsonQueue = new Set();

	// Failed handles cache (negative caching)
	/** @type {Map<string, number>} handle -> timestamp */
	#failedHandles = new Map();

	// Debounced batch processors (assigned in constructor)
	/** @type {() => void} */
	#debouncedHtmlBatch;
	/** @type {() => void} */
	#debouncedJsonBatch;

	/**
	 * @param {BatchRequestManagerOptions} [options={}]
	 */
	constructor({
		batchSize = 6,
		debounceTime = 100,
		fetchTimeout = 8000,
		maxCacheSize = 100,
		failureCacheDuration = 60000,
	} = {}) {
		this.#batchSize = batchSize;
		this.#fetchTimeout = fetchTimeout;
		this.#maxCacheSize = maxCacheSize;
		this.#failureCacheDuration = failureCacheDuration;

		this.#debouncedHtmlBatch = debounce(() => this.#processHtmlBatch(), debounceTime);
		this.#debouncedJsonBatch = debounce(() => this.#processJsonBatch(), debounceTime);
	}

	/**
	 * Request an HTML `<product-card>` snippet for a product handle.
	 * @param {string} handle
	 * @returns {Promise<Element|undefined>}
	 */
	addRequest(handle) {
		// Check negative cache
		if (this.#isRecentlyFailed(handle)) {
			return Promise.resolve(undefined);
		}

		// Return from cache
		if (this.#htmlCache.has(handle)) {
			const html = this.#htmlCache.get(handle);
			const fragment = document.createRange().createContextualFragment(html);
			return Promise.resolve(
				fragment.querySelector(`product-card[handle="${handle}"]`) ?? undefined,
			);
		}

		// Already in-flight - add to pending
		if (this.#htmlInFlight.has(handle)) {
			return new Promise((resolve) => this.#addPending(this.#htmlPending, handle, resolve));
		}

		// Queue for batch
		this.#htmlInFlight.add(handle);
		this.#htmlQueue.add(handle);

		return new Promise((resolve) => {
			this.#addPending(this.#htmlPending, handle, resolve);
			this.#debouncedHtmlBatch();
		});
	}

	/**
	 * Request JSON product data for a product handle.
	 * @param {string} handle
	 * @returns {Promise<ProductData|undefined>}
	 */
	addJSONRequest(handle) {
		// Check negative cache
		if (this.#isRecentlyFailed(handle)) {
			return Promise.resolve(undefined);
		}

		// Return from cache
		if (this.#jsonCache.has(handle)) {
			return Promise.resolve(this.#jsonCache.get(handle));
		}

		// Already in-flight - add to pending
		if (this.#jsonInFlight.has(handle)) {
			return new Promise((resolve) => this.#addPending(this.#jsonPending, handle, resolve));
		}

		// Queue for batch
		this.#jsonInFlight.add(handle);
		this.#jsonQueue.add(handle);

		return new Promise((resolve) => {
			this.#addPending(this.#jsonPending, handle, resolve);
			this.#debouncedJsonBatch();
		});
	}

	/**
	 * Process HTML batch queue.
	 * @private
	 */
	#processHtmlBatch() {
		this.#processBatch(this.#htmlQueue, (handles) =>
			this.#fetchBatch(handles, {
				urlSuffix: "?view=card",
				parseResponse: (res) => res.text(),
				onSuccess: (handle, text) => {
					const fragment = document.createRange().createContextualFragment(text);
					const card = fragment.querySelector(`product-card[handle="${handle}"]`);
					if (card) {
						this.#setCache(this.#htmlCache, handle, card.outerHTML);
						this.#resolve(this.#htmlPending, handle, (resolve) => resolve(card.cloneNode(true)));
					} else {
						this.#resolve(this.#htmlPending, handle, (resolve) => resolve(undefined));
					}
				},
				onError: (handle) => {
					this.#markFailed(handle);
					this.#resolve(this.#htmlPending, handle, (resolve) => resolve(undefined));
				},
				inFlightSet: this.#htmlInFlight,
			}),
		);
	}

	/**
	 * Process JSON batch queue.
	 * @private
	 */
	#processJsonBatch() {
		this.#processBatch(this.#jsonQueue, (handles) =>
			this.#fetchBatch(handles, {
				urlSuffix: "?view=product-data",
				parseResponse: (res) => res.json(),
				onSuccess: (handle, data) => {
					if (data) {
						this.#setCache(this.#jsonCache, handle, data);
						this.#resolve(this.#jsonPending, handle, (resolve) => resolve(data));
					} else {
						this.#resolve(this.#jsonPending, handle, (resolve) => resolve(undefined));
					}
				},
				onError: (handle) => {
					this.#markFailed(handle);
					this.#resolve(this.#jsonPending, handle, (resolve) => resolve(undefined));
				},
				inFlightSet: this.#jsonInFlight,
			}),
		);
	}

	/**
	 * Generic batch processor.
	 * @private
	 * @param {Set<string>} queue
	 * @param {(handles: string[]) => Promise<void>} fetchFn
	 */
	#processBatch(queue, fetchFn) {
		const batch = [...queue].slice(0, this.#batchSize);
		for (const handle of batch) queue.delete(handle);

		if (batch.length === 0) return;

		fetchFn(batch)
			.catch((err) => console.error("Batch error:", err))
			.finally(() => {
				if (queue.size > 0) this.#processBatch(queue, fetchFn);
			});
	}

	/**
	 * Generic fetch batch implementation.
	 * @private
	 * @param {string[]} handles
	 * @param {Object} options
	 * @param {string} options.urlSuffix
	 * @param {(res: Response) => Promise<any>} options.parseResponse
	 * @param {(handle: string, data: any) => void} options.onSuccess
	 * @param {(handle: string) => void} options.onError
	 * @param {Set<string>} options.inFlightSet
	 */
	async #fetchBatch(handles, { urlSuffix, parseResponse, onSuccess, onError, inFlightSet }) {
		const signal = AbortSignal.any([
			navAbortController.signal,
			AbortSignal.timeout(this.#fetchTimeout),
		]);

		await Promise.allSettled(
			handles.map(async (handle) => {
				try {
					const response = await fetch(
						`${globalThis.Shopify.routes.root}products/${handle}${urlSuffix}`,
						{
							signal,
						},
					);

					if (!response.ok) {
						throw new Error(`HTTP ${response.status}`);
					}

					const data = await parseResponse(response);
					scheduler.schedule(() => {
						onSuccess(handle, data);
						inFlightSet.delete(handle);
					});
				} catch (error) {
					if (error.name !== "AbortError") {
						console.error(`Fetch error for ${handle}:`, error);
					}
					onError(handle);
					inFlightSet.delete(handle);
				}
			}),
		);
	}

	/**
	 * Add a pending callback.
	 * @private
	 * @param {Map<string, Array<Function>>} pendingMap
	 * @param {string} handle
	 * @param {Function} resolve
	 */
	#addPending(pendingMap, handle, resolve) {
		if (!pendingMap.has(handle)) pendingMap.set(handle, []);
		pendingMap.get(handle).push(resolve);
	}

	/**
	 * Resolve all pending callbacks for a handle.
	 * @private
	 * @param {Map<string, Array<Function>>} pendingMap
	 * @param {string} handle
	 * @param {(resolve: Function) => void} callback
	 */
	#resolve(pendingMap, handle, callback) {
		const pending = pendingMap.get(handle);
		if (!pending) return;
		for (const resolve of pending) callback(resolve);
		pendingMap.delete(handle);
	}

	/**
	 * Set cache with LRU eviction.
	 * @private
	 * @param {Map<string, any>} cache
	 * @param {string} key
	 * @param {any} value
	 */
	#setCache(cache, key, value) {
		// LRU: delete and re-add to move to end
		if (cache.has(key)) cache.delete(key);
		cache.set(key, value);

		// Evict oldest if over limit
		if (cache.size > this.#maxCacheSize) {
			const oldest = cache.keys().next().value;
			cache.delete(oldest);
		}
	}

	/**
	 * Mark a handle as failed.
	 * @private
	 * @param {string} handle
	 */
	#markFailed(handle) {
		this.#failedHandles.set(handle, Date.now());
	}

	/**
	 * Check if a handle recently failed.
	 * @private
	 * @param {string} handle
	 * @returns {boolean}
	 */
	#isRecentlyFailed(handle) {
		const failedAt = this.#failedHandles.get(handle);
		if (!failedAt) return false;

		if (Date.now() - failedAt < this.#failureCacheDuration) {
			return true;
		}

		this.#failedHandles.delete(handle);
		return false;
	}
}

/**
 * Shared instance of the batch request manager.
 *
 * @example
 * // HTML card
 * productCardLoader.addRequest('my-handle').then((card) => {
 *   if (card) document.body.append(card);
 * });
 *
 * @example
 * // JSON data
 * productCardLoader.addJSONRequest('my-handle').then((data) => {
 *   if (data) console.log(data.title);
 * });
 */
export const productCardLoader = new BatchRequestManager({
	batchSize: 6,
	debounceTime: 100,
	fetchTimeout: 8000,
});
